import { IInventoryTaxListResponseData } from "@/api_framework/api_modals/Inventory";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import Input from "@/app/components/ui/Inputs/Input/index";
import { useInventoryTaxCreateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryTaxCreateAPI";
import { useInventoryTaxDetailsAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryTaxDetailsAPI";
import { useInventoryTaxUpdateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryTaxUpdateAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import { Switch } from "@headlessui/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

export interface IInventoryTaxFormFields {
  hsncode: string;
  cgst: number;
  sgst: number;
  igst: number;
  ugst: number;
  status: boolean;
}

const TaxAddComponent = () => {
  const navigate = useNavigate();
  const { forAlphaNumeric, forTaxRegex } = useFormValidations();
  const [params, _setparams] = useSearchParams();
  const { execute: createTaxAPI } = useInventoryTaxCreateAPI();
  const { taxDetails, execute: fetchTaxDetailsAPI } = useInventoryTaxDetailsAPI();
  const { execute: updateTaxDetais } = useInventoryTaxUpdateAPI();

  const formHook = useForm<IInventoryTaxFormFields>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    const taxId = params.get("tax_id");
    if (taxId) {
      fetchTaxDetailsAPI(taxId);
    }
  }, []);

  useEffect(() => {
    if (!taxDetails) return;
    if (taxDetails) {
      formHook.setValue("hsncode", taxDetails?.hsn_code);
      formHook.setValue("cgst", taxDetails?.cgst_percentage);
      formHook.setValue("igst", taxDetails?.igst_percentage);
      formHook.setValue("sgst", taxDetails?.sgst_percentage);
      formHook.setValue("ugst", taxDetails?.ugst_percentage);
      formHook.setValue("status", taxDetails?.status);
    }
  }, [taxDetails]);

  const validateConditionalFormFields = (data: IInventoryTaxFormFields) => {
    let isValid = false;
    if (data?.hsncode !== "" || data?.cgst || data?.igst || data?.sgst || data?.ugst) {
      isValid = true;
    }
    return isValid;
  };

  useEffect(() => {
    const value = formHook.watch("hsncode");
    const el = value.split("");
    if (el[0] === " " || el[1] === " ") {
      formHook.setValue("hsncode", ".");
    }
  }, [formHook.watch("hsncode")]);

  const onSubmit = (data: IInventoryTaxFormFields) => {
    const isFormSubmissionValid = validateConditionalFormFields(data);
    if (!isFormSubmissionValid) {
      return;
    }
    if (data && isFormSubmissionValid) {
      const constructedBody: IInventoryTaxListResponseData = {
        hsn_code: data?.hsncode,
        cgst_percentage: parseFloat(`${data?.cgst}`),
        igst_percentage: parseFloat(`${data?.igst}`),
        sgst_percentage: parseFloat(`${data?.sgst}`),
        ugst_percentage: parseFloat(`${data?.ugst}`),
        status: data?.status ? true : false,
      };
      if (taxDetails) {
        updateTaxDetais(constructedBody, taxDetails?.hsn_code).then(({ status }) => {
          if (status) {
            navigate(`/app/inventory/hsn-tax`);
          }
        });
      } else {
        createTaxAPI(constructedBody).then(({ status }) => {
          if (status) {
            navigate(`/app/inventory/hsn-tax`);
          }
        });
      }
    }
  };

  const handelBackToTaxList = () => {
    navigate(`/app/inventory/hsn-tax`);
  };

  return (
    <div className="rounded-md bg-white px-4 py-3">
      <form className="w-full mt-4" onSubmit={formHook.handleSubmit(onSubmit)}>
        <h2 className="text-base font-semibold">HSN & Tax Setup</h2>
        <div className="mt-4 sm:mt-6 flex flex-col gap-6 pb-24">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-[130px]">
              <p>
                HSN Code <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-3/6 lg:w-1/4">
              <Input
                className="text-xs"
                inputClassName="h-[38px] rounded-lg"
                placeholder="Enter HSN Code"
                maxLength={8}
                register={formHook.register("hsncode", {
                  required: true,
                  ...forAlphaNumeric.validations,
                })}
                fieldError={formHook.formState.errors.hsncode}
                errorMessages={[forAlphaNumeric.errors, { message: "HSN is required", type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-[130px]">
              <p>
                CGST (%) <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-3/6 lg:w-1/4">
              <Input
                className="text-xs"
                inputClassName="h-[38px] rounded-lg"
                maxLength={5}
                placeholder="Enter CGST Percentage"
                register={formHook.register("cgst", {
                  required: true,
                  ...forTaxRegex.validations,
                })}
                fieldError={formHook.formState.errors.cgst}
                errorMessages={[forTaxRegex.errors, { message: "CGST is required", type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-[130px]">
              <p>
                SGST (%) <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-3/6 lg:w-1/4">
              <Input
                className="text-xs"
                inputClassName="h-[38px] rounded-lg"
                maxLength={5}
                placeholder="Enter SGST Percentage"
                register={formHook.register("sgst", {
                  required: true,
                  ...forTaxRegex.validations,
                })}
                fieldError={formHook.formState.errors.sgst}
                errorMessages={[forTaxRegex.errors, { message: "SGST is required", type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-[130px]">
              <p>
                IGST (%) <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-3/6 lg:w-1/4">
              <Input
                maxLength={5}
                className="text-xs"
                inputClassName="h-[38px] rounded-lg"
                placeholder="Enter IGST Percentage"
                register={formHook.register("igst", {
                  required: true,
                  ...forTaxRegex.validations,
                })}
                fieldError={formHook.formState.errors.igst}
                errorMessages={[forTaxRegex.errors, { message: "IGST is required", type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-[130px]">
              <p>
                UGST (%) <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-3/6 lg:w-1/4">
              <Input
                maxLength={5}
                className="text-xs"
                inputClassName="h-[38px] rounded-lg"
                placeholder="Enter UGST Percentage"
                register={formHook.register("ugst", {
                  required: true,
                  ...forTaxRegex.validations,
                })}
                fieldError={formHook.formState.errors.ugst}
                errorMessages={[forTaxRegex.errors, { message: "UGST is required", type: "required" }]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-[130px]">
              <p>Status</p>
            </div>
            <div className="w-full sm:w-3/6 lg:w-1/4 flex gap-6 items-center">
              <div className="flex justify-start items-center">
                <Switch
                  checked={formHook.watch("status")}
                  onChange={() => {
                    if (formHook.watch("status")) {
                      formHook.setValue("status", false);
                    } else {
                      formHook.setValue("status", true);
                    }
                  }}
                  className={`${formHook.watch("status") ? "bg-[#4D9FFF]" : "bg-[#CCCCCC]"}
          relative inline-flex h-6 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75`}
                >
                  <span
                    aria-hidden="true"
                    className={`${formHook.watch("status") ? "translate-x-8" : "translate-x-0"}
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                  />
                </Switch>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end p-4 gap-4 items-center w-full border-t-2 right-0 bottom-0 fixed bg-white">
          <button type="button" className={SECONDARY_BUTTON} onClick={() => handelBackToTaxList()}>
            Cancel
          </button>
          <button type="submit" className={PRIMARY_BUTTON}>
            {taxDetails ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};
export default TaxAddComponent;
